import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Button, Card, Empty, Progress, Space, Spin, Switch, Tag, Tooltip, Typography, message } from 'antd'
import {
  AudioOutlined, AudioMutedOutlined, CheckCircleOutlined, CloseOutlined,
  DownloadOutlined, LeftOutlined, PauseCircleOutlined, PlayCircleOutlined,
  ReloadOutlined, RightOutlined, StopOutlined, VideoCameraOutlined,
} from '@ant-design/icons'
import useScreenRecorder from './useScreenRecorder'
import ClipEditor from './ClipEditor'
import { exportClips } from './ffmpegClient'
import { buildVttFromClips, parseVttToSegments } from './captions'
import { formatTime, inspectVideoBlob, inspectVideoFromUrl, isScreenCaptureSupported, makeId } from './utils'
import './screenRecording.css'

const { Text, Title } = Typography

/**
 * Robust screen-recording + clip/audio editing component.
 *
 * Fully self-contained: record one or more screen-capture clips, trim and
 * split them, mute/adjust their audio or replace it with a recorded
 * voiceover, arrange them in order, then export a single finished video.
 *
 * Does not know anything about "Documentation" or any particular save flow
 * — it hands the caller a plain `File` once the user is happy with it, so
 * it can be dropped into any upload form.
 *
 * Props:
 *   onComplete(file: File, meta: { durationSec, sizeBytes }) — required
 *   onCancel() — optional, called when the user backs out entirely
 *   onStatusChange({ status, elapsedSec, clipCount, hasFinal }) — optional,
 *     fired whenever recording status/progress changes. Lets a host that
 *     hides this component (e.g. a "minimize" view showing a small status
 *     pill while this stays mounted and recording in the background) show
 *     live status without needing to know anything else about it.
 *   initialClipUrl — optional. When set, the studio opens straight into the
 *     editor with this URL loaded as its first clip, instead of the empty
 *     "Start Recording" state. This is what lets a host let someone re-edit
 *     a recording that's already been saved/published (trim it further,
 *     mute/adjust its audio, add a voiceover) rather than only being able
 *     to record a brand new take from scratch. Works with any URL a
 *     <video> can play — an already-published S3 URL, or a local blob: URL
 *     for a not-yet-saved recording.
 *   initialClipDurationSec — optional hint used as a fallback if the
 *     video's own duration can't be read (e.g. a slow/blocked network).
 *   initialClipName — optional label for the seeded clip (defaults to
 *     "Existing recording").
 *   initialClipCaptionsVtt — optional WebVTT string (e.g. a previously
 *     published recording's captions) to seed onto the initial clip, so
 *     re-editing doesn't lose captions that were already generated.
 */
const ScreenRecordingStudio = ({
  onComplete, onCancel, onStatusChange,
  initialClipUrl, initialClipDurationSec, initialClipName, initialClipCaptionsVtt,
}) => {
  const [clips, setClips] = useState([])
  const [selectedClipId, setSelectedClipId] = useState(null)
  const [withMic, setWithMic] = useState(true)
  const [withCaptions, setWithCaptions] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [exportProgress, setExportProgress] = useState(0)
  const [exportStage, setExportStage] = useState('')
  const [finalBlob, setFinalBlob] = useState(null)
  const [finalUrl, setFinalUrl] = useState(null)
  const [finalCaptionsVtt, setFinalCaptionsVtt] = useState('')
  const [finalCaptionsUrl, setFinalCaptionsUrl] = useState(null)
  const [seeding, setSeeding] = useState(!!initialClipUrl)

  const supported = useMemo(isScreenCaptureSupported, [])
  const clipsRef = useRef(clips)
  clipsRef.current = clips

  const handleClipReady = useCallback(async (blob, meta) => {
    const { url, durationSec, thumbnailDataUrl } = await inspectVideoBlob(blob)
    const clip = {
      id: makeId('clip'),
      blob,
      url,
      name: `Recording ${clipsRef.current.length + 1}`,
      durationSec,
      inSec: 0,
      outSec: durationSec,
      muted: false,
      volume: 1,
      // Whether the raw capture has an audio track at all — screen-only
      // captures with no system audio and no mic won't. Defaults to true
      // (rather than guessing false) if a caller doesn't report it.
      hasAudio: meta?.hasAudio !== false,
      voiceoverBlob: null,
      voiceoverUrl: null,
      voiceoverMode: null,
      voiceoverVolume: 1,
      // Timestamped { id, start, end, text } lines from live speech
      // recognition during recording (empty if captions were off/
      // unsupported) — editable afterwards in ClipEditor.
      captions: meta?.captions || [],
      thumbnailDataUrl,
    }
    setClips((cs) => [...cs, clip])
    setSelectedClipId(clip.id)
    message.success(
      meta?.captions?.length
        ? `Recording captured with ${meta.captions.length} caption line${meta.captions.length > 1 ? 's' : ''} — trim it below or record another clip.`
        : 'Recording captured — trim it below or record another clip.'
    )
  }, [])

  const handleRecorderError = useCallback((err) => {
    if (err?.name === 'NotAllowedError') {
      message.error('Screen recording permission was denied.')
    } else {
      message.error(err?.message || 'Screen recording failed.')
    }
  }, [])

  const recorder = useScreenRecorder({ onClipReady: handleClipReady, onError: handleRecorderError })

  // Seed the editor with an already-existing recording (e.g. reopening a
  // published documentation entry to trim/re-edit it) instead of the empty
  // "Start Recording" state. Runs once per mount — a host wanting this
  // re-triggered gives the studio a fresh `key` rather than changing the
  // prop in place.
  useEffect(() => {
    if (!initialClipUrl) return
    let cancelled = false
    ;(async () => {
      try {
        const { url, durationSec, thumbnailDataUrl, hasAudio } = await inspectVideoFromUrl(initialClipUrl)
        if (cancelled) return
        const resolvedDuration = durationSec || initialClipDurationSec || 0
        const clip = {
          id: makeId('clip'),
          // A plain URL string works fine here too — ffmpeg's fetchFile
          // (used at export time in ffmpegClient.js) accepts a Blob, a File,
          // or a URL/path just the same.
          blob: initialClipUrl,
          url,
          name: initialClipName || 'Existing recording',
          durationSec: resolvedDuration,
          inSec: 0,
          outSec: resolvedDuration,
          muted: false,
          volume: 1,
          hasAudio,
          voiceoverBlob: null,
          voiceoverUrl: null,
          voiceoverMode: null,
          voiceoverVolume: 1,
          captions: parseVttToSegments(initialClipCaptionsVtt),
          thumbnailDataUrl,
        }
        setClips((cs) => (cs.length ? cs : [clip]))
        setSelectedClipId((id) => id ?? clip.id)
      } catch {
        if (!cancelled) message.error('Could not load the existing recording for editing.')
      } finally {
        if (!cancelled) setSeeding(false)
      }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    onStatusChange && onStatusChange({
      status: recorder.status,
      elapsedSec: recorder.elapsedSec,
      clipCount: clips.length,
      hasFinal: !!finalBlob,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recorder.status, recorder.elapsedSec, clips.length, finalBlob])

  // Release every object URL we created when the studio unmounts.
  useEffect(() => () => {
    clipsRef.current.forEach((c) => {
      URL.revokeObjectURL(c.url)
      if (c.voiceoverUrl) URL.revokeObjectURL(c.voiceoverUrl)
    })
    if (finalUrl) URL.revokeObjectURL(finalUrl)
    if (finalCaptionsUrl) URL.revokeObjectURL(finalCaptionsUrl)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const updateClip = (id, patch) => {
    setClips((cs) => cs.map((c) => (c.id === id ? { ...c, ...patch } : c)))
  }

  const deleteClip = (id) => {
    setClips((cs) => {
      const target = cs.find((c) => c.id === id)
      const rest = cs.filter((c) => c.id !== id)
      if (target) {
        const blobStillUsed = rest.some((c) => c.blob === target.blob)
        if (!blobStillUsed) URL.revokeObjectURL(target.url)
        if (target.voiceoverUrl) URL.revokeObjectURL(target.voiceoverUrl)
      }
      if (selectedClipId === id) setSelectedClipId(rest[0]?.id ?? null)
      return rest
    })
  }

  const splitClip = (id, atSec) => {
    setClips((cs) => {
      const idx = cs.findIndex((c) => c.id === id)
      if (idx === -1) return cs
      const original = cs[idx]
      const first = { ...original, id: makeId('clip'), outSec: atSec, name: `${original.name} (1)` }
      const second = {
        ...original,
        id: makeId('clip'),
        inSec: atSec,
        name: `${original.name} (2)`,
        // A voiceover recorded for the whole clip doesn't cleanly split — keep it on the first half only.
        voiceoverBlob: null,
        voiceoverUrl: null,
        voiceoverMode: null,
      }
      const next = [...cs]
      next.splice(idx, 1, first, second)
      setSelectedClipId(first.id)
      return next
    })
  }

  const moveClip = (id, dir) => {
    setClips((cs) => {
      const idx = cs.findIndex((c) => c.id === id)
      const swapWith = idx + dir
      if (idx === -1 || swapWith < 0 || swapWith >= cs.length) return cs
      const next = [...cs]
      ;[next[idx], next[swapWith]] = [next[swapWith], next[idx]]
      return next
    })
  }

  const totalTrimmedDuration = clips.reduce((sum, c) => sum + Math.max(0, c.outSec - c.inSec), 0)

  const handleExport = async () => {
    if (!clips.length) return
    setExporting(true)
    setExportProgress(0)
    setExportStage('Loading video engine…')
    try {
      const blob = await exportClips(clips, {
        onProgress: setExportProgress,
        onStage: setExportStage,
      })
      const vtt = buildVttFromClips(clips)
      setFinalBlob(blob)
      setFinalUrl(URL.createObjectURL(blob))
      setFinalCaptionsVtt(vtt)
      setFinalCaptionsUrl(vtt ? URL.createObjectURL(new Blob([vtt], { type: 'text/vtt' })) : null)
    } catch (err) {
      message.error(err?.message || 'Failed to export the recording.')
    } finally {
      setExporting(false)
    }
  }

  const handleUseRecording = () => {
    if (!finalBlob) return
    const file = new File([finalBlob], `screen-recording-${Date.now()}.webm`, { type: 'video/webm' })
    onComplete && onComplete(file, { durationSec: totalTrimmedDuration, sizeBytes: finalBlob.size, captionsVtt: finalCaptionsVtt })
  }

  const handleDownload = () => {
    if (!finalUrl) return
    const a = document.createElement('a')
    a.href = finalUrl
    a.download = `screen-recording-${Date.now()}.webm`
    document.body.appendChild(a)
    a.click()
    a.remove()
  }

  const handleReEdit = () => {
    if (finalUrl) URL.revokeObjectURL(finalUrl)
    if (finalCaptionsUrl) URL.revokeObjectURL(finalCaptionsUrl)
    setFinalBlob(null)
    setFinalUrl(null)
    setFinalCaptionsVtt('')
    setFinalCaptionsUrl(null)
  }

  const selectedClip = clips.find((c) => c.id === selectedClipId) || null
  const isBusy = recorder.status === 'recording' || recorder.status === 'paused' || recorder.status === 'requesting'

  if (!supported) {
    return (
      <Alert
        type='error'
        showIcon
        message='Screen recording is not available'
        description='Your browser does not support screen capture here. Please use an up-to-date Chrome, Edge, or Firefox over HTTPS (or localhost).'
      />
    )
  }

  if (finalBlob) {
    return (
      <Card className='sr-studio' title={<Space><CheckCircleOutlined style={{ color: '#52c41a' }} />Recording ready</Space>}>
        <video src={finalUrl} controls style={{ width: '100%', maxHeight: 420, background: '#000', borderRadius: 8 }}>
          {finalCaptionsUrl && <track kind='subtitles' src={finalCaptionsUrl} srcLang='en' label='Captions' default />}
        </video>
        <div style={{ marginTop: 8 }}>
          <Text type='secondary' style={{ fontSize: 12 }}>
            Duration: {formatTime(totalTrimmedDuration)} · Size: {(finalBlob.size / (1024 * 1024)).toFixed(1)} MB
            {finalCaptionsVtt && ` · Captions included`}
          </Text>
        </div>
        <Space style={{ marginTop: 16 }}>
          <Button type='primary' icon={<CheckCircleOutlined />} onClick={handleUseRecording}>Use This Recording</Button>
          <Button icon={<DownloadOutlined />} onClick={handleDownload}>Download</Button>
          <Button icon={<ReloadOutlined />} onClick={handleReEdit}>Re-edit</Button>
          {onCancel && <Button icon={<CloseOutlined />} onClick={onCancel}>Cancel</Button>}
        </Space>
      </Card>
    )
  }

  return (
    <div className='sr-studio'>
      {/* Capture controls */}
      <Card size='small' style={{ borderRadius: 10, marginBottom: 16 }}>
        <div className='sr-capture-bar'>
          <Space size={16} align='center' wrap>
            {recorder.status === 'idle' && (
              <Button type='primary' size='large' icon={<VideoCameraOutlined />} onClick={() => recorder.start({ withMic, withCaptions })}>
                Start Recording
              </Button>
            )}
            {recorder.status === 'requesting' && (
              <Button type='primary' size='large' loading>Waiting for permission…</Button>
            )}
            {(recorder.status === 'recording' || recorder.status === 'paused') && (
              <Space>
                <Tag color='red' className='sr-rec-dot'>● {recorder.status === 'paused' ? 'PAUSED' : 'REC'}</Tag>
                <Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>{formatTime(recorder.elapsedSec)}</Text>
                {recorder.supportsPause && (
                  recorder.status === 'recording'
                    ? <Button icon={<PauseCircleOutlined />} onClick={recorder.pause}>Pause</Button>
                    : <Button icon={<PlayCircleOutlined />} onClick={recorder.resume}>Resume</Button>
                )}
                <Button danger icon={<StopOutlined />} onClick={recorder.stop}>Stop</Button>
                {recorder.micAvailable && (
                  <Tooltip title={recorder.micEnabled ? 'Mute microphone' : 'Unmute microphone'}>
                    <Button
                      icon={recorder.micEnabled ? <AudioOutlined /> : <AudioMutedOutlined />}
                      onClick={() => recorder.setMicEnabled(!recorder.micEnabled)}
                    />
                  </Tooltip>
                )}
              </Space>
            )}

            {!isBusy && (
              <Space size={6}>
                <Switch size='small' checked={withMic} onChange={setWithMic} />
                <Text type='secondary' style={{ fontSize: 13 }}>Record microphone</Text>
              </Space>
            )}

            {!isBusy && (
              <Tooltip
                title={
                  !recorder.captionsSupported
                    ? 'Live captions need Chrome or Edge — you can still add caption lines by hand after recording.'
                    : !withMic
                      ? 'Turn on the microphone to auto-generate captions — captions can only transcribe mic speech.'
                      : 'Automatically transcribe your voice into captions as you record.'
                }
              >
                <Space size={6}>
                  <Switch
                    size='small'
                    checked={withCaptions && withMic && recorder.captionsSupported}
                    disabled={!withMic || !recorder.captionsSupported}
                    onChange={setWithCaptions}
                  />
                  <Text type='secondary' style={{ fontSize: 13 }}>Auto-generate captions</Text>
                </Space>
              </Tooltip>
            )}
          </Space>
        </div>
      </Card>

      {/* Clip strip + editor */}
      {seeding ? (
        <div style={{ textAlign: 'center', margin: '40px 0' }}>
          <Spin />
          <div style={{ marginTop: 8 }}><Text type='secondary'>Loading the existing recording…</Text></div>
        </div>
      ) : clips.length === 0 ? (
        <Empty description='No clips recorded yet. Click "Start Recording" to capture your screen.' style={{ margin: '40px 0' }} />
      ) : (
        <>
          <div className='sr-clip-strip'>
            {clips.map((clip, i) => (
              <div
                key={clip.id}
                className={`sr-clip-thumb ${selectedClipId === clip.id ? 'is-selected' : ''}`}
                onClick={() => setSelectedClipId(clip.id)}
              >
                {clip.thumbnailDataUrl
                  ? <img src={clip.thumbnailDataUrl} alt={clip.name} />
                  : <div className='sr-clip-thumb-fallback'><VideoCameraOutlined /></div>}
                <div className='sr-clip-thumb-meta'>
                  <Text ellipsis style={{ fontSize: 12, maxWidth: 100 }}>
                    {clip.captions?.length > 0 ? 'CC ' : ''}{clip.name}
                  </Text>
                  <Text type='secondary' style={{ fontSize: 11 }}>{formatTime(clip.outSec - clip.inSec)}</Text>
                </div>
                <div className='sr-clip-thumb-actions' onClick={(e) => e.stopPropagation()}>
                  <Button size='small' type='text' icon={<LeftOutlined />} disabled={i === 0} onClick={() => moveClip(clip.id, -1)} />
                  <Button size='small' type='text' icon={<RightOutlined />} disabled={i === clips.length - 1} onClick={() => moveClip(clip.id, 1)} />
                </div>
              </div>
            ))}
            <div className='sr-clip-thumb sr-clip-thumb-add' onClick={() => !isBusy && recorder.start({ withMic, withCaptions })}>
              <VideoCameraOutlined style={{ fontSize: 20 }} />
              <Text style={{ fontSize: 12 }}>Record Another</Text>
            </div>
          </div>

          {selectedClip && (
            <div style={{ marginTop: 16 }}>
              {/* Keyed by clip id so switching clips (or splitting one into
                  two) remounts the editor instead of reusing its internal
                  playhead/play state for a different video. */}
              <ClipEditor
                key={selectedClip.id}
                clip={selectedClip}
                onChange={(patch) => updateClip(selectedClip.id, patch)}
                onSplit={(atSec) => splitClip(selectedClip.id, atSec)}
                onDelete={() => deleteClip(selectedClip.id)}
              />
            </div>
          )}
        </>
      )}

      {/* Export */}
      <div style={{ marginTop: 20, borderTop: '1px solid #E4E7EC', paddingTop: 16 }}>
        {exporting ? (
          <div style={{ maxWidth: 420 }}>
            <Text>{exportStage}</Text>
            <Progress percent={Math.round(exportProgress * 100)} status='active' />
          </div>
        ) : (
          <Space>
            <Button
              type='primary'
              size='large'
              icon={<CheckCircleOutlined />}
              disabled={clips.length === 0 || isBusy}
              onClick={handleExport}
            >
              Export &amp; Use Recording
            </Button>
            {onCancel && <Button size='large' icon={<CloseOutlined />} onClick={onCancel}>Cancel</Button>}
            {clips.length > 0 && (
              <Text type='secondary' style={{ fontSize: 12 }}>
                {clips.length} clip{clips.length > 1 ? 's' : ''} · total {formatTime(totalTrimmedDuration)}
              </Text>
            )}
          </Space>
        )}
      </div>
    </div>
  )
}

export default ScreenRecordingStudio
